import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Tracks real network connectivity.
 *
 * `navigator.onLine` is unreliable — it can be true while all requests fail
 * (e.g. captive portal, firewall, flaky mobile signal). We use it as the
 * initial signal, then correct it based on actual upload success/failure.
 */
export function useNetworkStatus(): {
  isOnline: boolean
  reportSuccess: () => void
  reportFailure: () => void
} {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)
  // Track consecutive failures to avoid flapping on a single blip
  const failureCount = useRef(0)

  useEffect(() => {
    const handleOnline = (): void => {
      failureCount.current = 0
      setIsOnline(true)
    }
    const handleOffline = (): void => {
      setIsOnline(false)
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  /** Call this when a network request succeeds. */
  const reportSuccess = useCallback((): void => {
    failureCount.current = 0
    setIsOnline(true)
  }, [])

  /**
   * Call this when a network request fails.
   * We require 2 consecutive failures before marking offline to avoid
   * flapping on transient errors.
   */
  const reportFailure = useCallback((): void => {
    failureCount.current += 1
    if (failureCount.current >= 2) {
      setIsOnline(false)
    }
  }, [])

  return { isOnline, reportSuccess, reportFailure }
}
